declare const gon: any;

const VISIT_ID_KEY: string = "aob_visit_id";

/**
 * Generates an RFC4122 v4-ish UUID without any external dependency.
 */
function generateUuid(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c: string): string => {
        const r: number = Math.random() * 16 | 0;
        const v: number = c === "x" ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Returns the current visit's id, generating and persisting one in
 * sessionStorage on first use. One id per browser tab/visit, so funnel
 * events fired before a Search exists (clicked_price, landing_page_loaded)
 * can still be tied together with later ones.
 */
export function getVisitId(): string {
    try {
        let visitId: string = sessionStorage.getItem(VISIT_ID_KEY);

        if (!visitId) {
            visitId = generateUuid();
            sessionStorage.setItem(VISIT_ID_KEY, visitId);
        }

        return visitId;
    } catch (_e) {
        // sessionStorage can throw (Safari private mode, some webviews) -
        // fall back so this never blocks the caller (e.g. an error popup).
        return generateUuid();
    }
}

/**
 * Called once per page load (packs/vue_helper.js) so the visit_id exists
 * before any funnel event needs to reference it.
 */
export function initFunnelTracking(): void {
    getVisitId();
}

let landingRenderedAt: number = null;

function landingKey(searchId: number | string): string {
    return `aob_funnel_landing_${searchId}`;
}

/**
 * Marks the moment the landing page (step1) finished rendering, so later
 * events can report duration since landing. Persisted in sessionStorage
 * per search_id so a mobile location.reload() (step_loading.js, once a
 * pending search becomes ready) doesn't reset it.
 *
 * @returns false if this search's landing was already marked (e.g. this
 *   is that reload) - callers use this to avoid re-sending events that
 *   already fired once.
 */
export function markLandingRendered(searchId: number | string): boolean {
    try {
        const key = landingKey(searchId);
        const existing = sessionStorage.getItem(key);

        if (existing) {
            landingRenderedAt = Number(existing);
            return false;
        }

        landingRenderedAt = Date.now();
        sessionStorage.setItem(key, landingRenderedAt.toString());
        return true;
    } catch (_e) {
        // sessionStorage can throw (see getVisitId) - degrade to
        // in-memory-only rather than breaking the whole page.
        landingRenderedAt = Date.now();
        return true;
    }
}

export function getLandingRenderedAt(): number {
    return landingRenderedAt;
}

function detectDevice(): string {
    return /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop";
}

interface FunnelEventData {
    search_id?: number | string;
    clicked_price?: number;
    duration_ms?: number;
    error_type?: string;
    message?: string;
}

/**
 * Logs a funnel-tracking event to Elasticsearch (via /funnel_events -> F-ENGINE
 * -> the "funnel_events" index), correlated by visit_id.
 *
 * No-ops entirely for non-meta visits (gon.from_meta falsy) - this funnel is
 * specifically about meta clickout performance, so visits that never came
 * from a meta clickout aren't sent at all, not just tagged. Reads gon.from_meta
 * directly (not a module-level flag set by another entry point) because each
 * webpack pack is a separate bundle with its own copy of this module - a flag
 * set from vue_helper.js's copy would never be visible here. gon itself is a
 * real global (window.gon), so every bundle sees the same, correct value.
 */
export function logFunnelEvent(event: string, data: FunnelEventData = {}): void {
    if (!gon.from_meta) {
        return;
    }

    $.post("/funnel_events", {
        visit_id: getVisitId(),
        device: detectDevice(),
        event: event,
        from_meta: gon.from_meta,
        url: document.location.href,
        search_id: data.search_id,
        clicked_price: data.clicked_price,
        duration_ms: data.duration_ms,
        error_type: data.error_type,
        message: data.message
    });
}

// Matches anything that opens an offer's detail overlay. Was
// `input[type="submit"]` inside `.itinerary` -- markup from the pre-rebuild
// result card. The rebuilt card (front/_step_1_result.html.erb) has neither:
// its CTA is a <button type="button"> and no ancestor carries `.itinerary`,
// so markInteracted never matched and NO interaction event has been sent on
// the new design at all. Since bounce is derived as
// 100% - (interaction / search_results_rendered), meta traffic reads as ~100%
// bounce regardless of what customers actually do.
//
// Note this also widens the definition slightly, deliberately: the rebuilt
// card root carries the same showDetail action as the button, because mobile
// hides the button entirely and makes the whole card the tap target. So
// "clicked an offer's own button" becomes "opened an offer" -- on mobile
// there is no button to click, and tapping the card is the only way through.
// The (i) fare-rules toggle calls stopPropagation (step1_results_controller.js
// #toggleRules), so it never reaches this document-level listener and still
// does not count as an interaction.
const OFFER_TRIGGER_SELECTOR: string = '[data-action*="step1-results#showDetail"]';
const INTERACTION_EVENTS: string[] = ["click", "touchstart"];

let hasInteracted: boolean = false;

function markInteracted(event: Event): void {
    if (hasInteracted) {
        return;
    }

    const target = event.target as Element;
    if (!target || typeof target.closest !== "function" || !target.closest(OFFER_TRIGGER_SELECTOR)) {
        return;
    }

    hasInteracted = true;
    INTERACTION_EVENTS.forEach((eventName: string): void => {
        document.removeEventListener(eventName, markInteracted);
    });

    logFunnelEvent("interaction");
}

/**
 * Wires up interaction detection for the current page: a click or tap that
 * opens an offer's detail overlay - the "Visa resa" button on desktop, or
 * the card itself on mobile, where that button is hidden - reports a single
 * "interaction" event. Both carry the same
 * `data-action="click->step1-results#showDetail"`, which is what
 * OFFER_TRIGGER_SELECTOR keys off. Clicking page chrome, filters or sort
 * does not count; nor does the (i) fare-rules toggle, which stops
 * propagation before this listener sees it.
 *
 * bounce_no_interaction is intentionally NOT computed here client-side -
 * there is no reliable way to catch "the visit ended" in every browser
 * (visibilitychange/pagehide support and timing vary, sendBeacon has its
 * own limits). Instead: bounce rate = 100% - (visits with "interaction"
 * among visits with "search_results_rendered"), computed in Kibana. The
 * absence of an interaction event is the signal; nothing needs to fire
 * at the moment the visitor leaves.
 */
export function initInteractionTracking(): void {
    INTERACTION_EVENTS.forEach((eventName: string): void => {
        document.addEventListener(eventName, markInteracted, { passive: true });
    });
}
